import axios from 'axios';
import { useAuthStore } from '../store/useAuthStore';
import { shouldLogoutUser } from '../utils/subscriptionUtils';

const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';

// Set up axios response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear authentication state and redirect to login
      const { logout } = useAuthStore.getState();
      logout();
    }
    return Promise.reject(error);
  }
);

export const setUserPinCode = async (pin: string) => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.post(`${SERVER_URL}/api/product-user/set-pin`, { pin }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    // Handle error as needed
    throw error;
  }
};

export const deleteUserAccount = async () => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.delete(`${SERVER_URL}/api/product-user/delete-account`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendFeedback = async (rating: number, message: string) => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.post(`${SERVER_URL}/api/product-user/send-feedback`,
      { rating, message },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface User {
  id: number;
  email: string;
  isPinSetUp: boolean;
  username?: string;
  lang?: string;
}

export const getCurrentUser = async (): Promise<{ user: User }> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get(`${SERVER_URL}/api/product-user/me`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    // Check subscription status and logout if expired
    if (response.data && response.data.user) {
      try {
        const subscriptionData = await getCurrentUserSub(response.data.user.email);

        if (subscriptionData.subscription && subscriptionData.subscription.length > 0) {
          const subscription = subscriptionData.subscription[0];

          if (shouldLogoutUser(subscription)) {
            console.log('Subscription expired or inactive, logging out user');
            const { logout } = useAuthStore.getState();
            logout();
            throw new Error('Subscription has expired. User has been logged out.');
          }
        } else {
          // No subscription found, logout user
          console.log('No subscription found, logging out user');
          const { logout } = useAuthStore.getState();
          logout();
          throw new Error('No active subscription found. User has been logged out.');
        }
      } catch (subscriptionError) {
        // If we can't fetch subscription due to network issues, continue
        // But if it's an auth error (401), let the axios interceptor handle it
        if (subscriptionError instanceof Error) {
          if (subscriptionError.message.includes('401')) {
            throw subscriptionError;
          }
          if (subscriptionError.message.includes('logged out')) {
            // This is our own logout error, re-throw it
            throw subscriptionError;
          }
        }
        console.warn('Could not verify subscription status:', subscriptionError);
      }
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface Certificate {
  id: number;
  name: string;
  issueDate: string;
  courseId: string;
  courseName: string;
  certificateUrl?: string;
}
interface StripePrice {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  livemode: boolean;
  nickname: string | null;
  product: string;
  recurring: {
    interval: string;
    interval_count: number;
    trial_period_days: number | null;
    usage_type: string;
  };
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

interface StripeSubscriptionItem {
  id: string;
  object: string;
  created: number;
  current_period_end: number;
  current_period_start: number;
  price: StripePrice;
  quantity: number;
  subscription: string;
}

export interface StripeSubscription {
  id: string;
  object: string;
  billing_cycle_anchor: number;
  cancel_at: number | null;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  created: number;
  currency: string;
  customer: string;
  items: {
    object: string;
    data: StripeSubscriptionItem[];
    has_more: boolean;
    total_count: number;
  };
  status: string;
  current_period_end?: number;
  current_period_start?: number;
}

export interface UserSubscriptionResponse {
  subscription: StripeSubscription[];
}
export const getCurrentUserSub = async (email: string): Promise<UserSubscriptionResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(`${SERVER_URL}/api/user-subscription?email=${email}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    throw error;
  }
};

export const cancelSubscription = async (subscriptionExposedId: string) => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.delete(`${SERVER_URL}/api/subscriptions/${subscriptionExposedId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};
